import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import { useAuth } from "../../stores/auth";
import { needsPhoneVerification } from "../../lib/phoneVerification";
import { toast } from "../../stores/toast";
import { Button, Input } from "../../components/ui";
import { PolicyConsentCheckbox } from "../../components/PolicyConsentCheckbox";
import { SlabWall } from "../../components/fx";

type RegisterStep = "email" | "details";

interface LoginPrefetchProfile {
  email: string;
  displayName: string;
  avatarUrl: string | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value: string): string {
  return value.toLowerCase().trim();
}

function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(normalizeEmail(value));
}

async function fetchEmailAvailability(email: string): Promise<LoginPrefetchProfile | null> {
  const data = await api<{ profile: LoginPrefetchProfile | null }>("/auth/login/prefetch", {
    body: { email, client: "storefront" },
  });
  return data.profile;
}

export function Register() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const register = useAuth((s) => s.register);

  const [step, setStep] = useState<RegisterStep>("email");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptPolicies, setAcceptPolicies] = useState(false);
  const [busy, setBusy] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);

  const normalizedEmail = normalizeEmail(email);

  const prefetchQuery = useQuery({
    queryKey: ["register-prefetch", normalizedEmail],
    queryFn: () => fetchEmailAvailability(normalizedEmail),
    enabled: step === "details" && isValidEmail(email),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (step === "details") {
      displayNameRef.current?.focus();
    } else {
      emailRef.current?.focus();
    }
  }, [step]);

  function goToDetailsStep() {
    if (!isValidEmail(email)) {
      toast("error", "Enter a valid email address");
      emailRef.current?.focus();
      return;
    }
    const trimmed = normalizeEmail(email);
    setEmail(trimmed);
    setStep("details");
    void queryClient.prefetchQuery({
      queryKey: ["register-prefetch", trimmed],
      queryFn: () => fetchEmailAvailability(trimmed),
      staleTime: 5 * 60_000,
    });
  }

  function goToEmailStep() {
    setStep("email");
    setPassword("");
    setAcceptPolicies(false);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!displayName.trim()) {
      toast("error", "Enter a display name");
      displayNameRef.current?.focus();
      return;
    }
    if (password.length < 8) {
      toast("error", "Password must be at least 8 characters");
      return;
    }
    if (!acceptPolicies) {
      toast("error", "Accept the Terms & Conditions and Privacy Policy to continue");
      return;
    }
    setBusy(true);
    try {
      await register(normalizedEmail, password, displayName.trim(), true);
      toast("success", "Welcome to Mr Gaole! Check your inbox to verify your email.");
      const { user, config } = useAuth.getState();
      if (needsPhoneVerification(user, config)) {
        navigate("/account/verify-phone", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Registration failed");
    } finally {
      setBusy(false);
    }
  }

  const existingProfile = prefetchQuery.data;
  const prefetchLoading = step === "details" && prefetchQuery.isFetching && !prefetchQuery.data;

  return (
    <div className="relative flex min-h-[70vh] flex-col justify-center overflow-hidden px-6 py-8">
      <SlabWall />
      <div className="relative mx-auto w-full max-w-md">
        <div className="glass-panel neon-ring rounded-2xl p-6 shadow-xl sm:p-8">
          <img src="/logo-wordmark.png" alt="Mr Gaole" className="h-9 w-auto max-w-[180px] object-contain object-left" />

          {step === "email" ? (
            <>
              <h1 className="mt-6 font-display text-2xl font-bold">Create account</h1>
              <p className="mt-1 text-sm text-white/50">Packs, raffles, auctions — all in one place</p>
              <form
                className="mt-6 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  goToDetailsStep();
                }}
              >
                <Input
                  ref={emailRef}
                  type="email"
                  label="Email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="flex justify-end pt-1">
                  <Button type="submit">Next</Button>
                </div>
              </form>
              <p className="mt-5 text-center text-xs text-white/50">
                Already a collector?{" "}
                <Link to="/login" className="font-semibold text-brand-pink hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          ) : (
            <>
              <h1 className="mt-6 font-display text-2xl font-bold">Set up your profile</h1>
              <p className="mt-1 text-sm text-white/50">Choose a display name and password</p>

              <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-sm font-bold text-white">
                    {normalizedEmail.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Email</p>
                    <p className="truncate text-sm font-medium">{normalizedEmail}</p>
                    {prefetchLoading && (
                      <p className="text-xs text-white/40">Checking email…</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={goToEmailStep}
                    className="shrink-0 text-xs font-semibold text-brand-pink hover:underline"
                  >
                    Change
                  </button>
                </div>
              </div>

              {existingProfile && (
                <p className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200/90">
                  This email may already be registered.{" "}
                  <Link to="/login" className="font-semibold text-brand-pink hover:underline">
                    Sign in instead
                  </Link>
                </p>
              )}

              <form className="mt-5 space-y-4" onSubmit={submit}>
                <Input
                  ref={displayNameRef}
                  label="Display name"
                  placeholder="How we should address you"
                  autoComplete="nickname"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <Input
                  type="password"
                  label="Password"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PolicyConsentCheckbox
                  variant="register"
                  checked={acceptPolicies}
                  onChange={setAcceptPolicies}
                />
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={goToEmailStep}
                    className="text-sm font-medium text-brand-pink hover:underline"
                  >
                    Back
                  </button>
                  <Button type="submit" disabled={busy || !acceptPolicies}>
                    {busy ? "Creating account…" : "Create account"}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
